import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axiosInstance as publicAxiosInstance } from "api/axios";
import { createAxiosInstance } from "api/axiosConfig";
import { byteLength, truncateToBytes } from "utils/textBytes";

const getBaseUrl = (tenantVal) => `${tenantVal}/video/meeting`;

// Patients joining from a link have no staff session and no stored tenant.
// "0" matches the fallback participant-join uses.
const PARTICIPANT_TENANT_FALLBACK = "0";

// The backend finds the appointment from the meeting for reports without a
// login, and expects tenant 0 in the path for them.
const PATIENT_REPORT_TENANT = "0";

// report-error refuses errorMessage over 16 KB; half that leaves headroom.
// Diagnostics build messages to fit; this is the last line for every report.
export const REPORT_MESSAGE_MAX_BYTES = 8000;

const capReportMessage = (message) => {
  const text = typeof message === "string" ? message : String(message ?? "");

  return byteLength(text) <= REPORT_MESSAGE_MAX_BYTES
    ? text
    : truncateToBytes(text, REPORT_MESSAGE_MAX_BYTES);
};

/**
 * Get meeting health status
 * GET /{tenant}/video/meeting/{meetingId}/health
 */
export const useGetMeetingHealth = (meetingId, options = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["meetingHealth", meetingId],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${getBaseUrl(tenantVal)}/${meetingId}/health`
      );
      return response.data;
    },
    enabled: !!tenantVal && !!meetingId && options.enabled !== false,
    refetchInterval: options.refetchInterval || 10000, // Poll every 10 seconds
    staleTime: 5000,
    retry: 1,
  });
};

/**
 * Reconnect to meeting
 * POST /{tenant}/video/meeting/{meetingId}/reconnect
 */
export const useReconnectMeeting = () => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ meetingId, attendeeId, externalUserId, reason }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${getBaseUrl(tenantVal)}/${meetingId}/reconnect`,
        {
          attendeeId,
          externalUserId,
          reason,
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Update any cached credentials
      if (data.joinToken) {
        queryClient.invalidateQueries({ queryKey: ["meetingHealth", data.meetingId] });
      }
    },
  });
};

/**
 * Pre-flight check before joining
 * GET /{tenant}/video/meeting/{meetingId}/preflight
 */
export const usePreflightCheck = (meetingId, options = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["meetingPreflight", meetingId],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${getBaseUrl(tenantVal)}/${meetingId}/preflight`
      );
      return response.data;
    },
    enabled: !!tenantVal && !!meetingId && options.enabled !== false,
    staleTime: 30000, // Cache for 30 seconds
    retry: 2,
  });
};

/**
 * Refresh meeting token
 * POST /{tenant}/video/meeting/{meetingId}/refresh-token
 */
export const useRefreshToken = () => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ meetingId, attendeeId, externalUserId }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${getBaseUrl(tenantVal)}/${meetingId}/refresh-token`,
        {
          attendeeId,
          externalUserId,
        }
      );
      return response.data;
    },
  });
};

/**
 * Report client-side error
 * POST /{tenant}/video/meeting/report-error
 */
export const useReportVideoError = () => {
  const tenantVal =
    localStorage.getItem("kinesin-tenant") || PARTICIPANT_TENANT_FALLBACK;

  return useMutation({
    mutationFn: async ({
      meetingId,
      attendeeId,
      appointmentId,
      errorType,
      errorMessage,
      browserInfo,
      reconnectionAttempts,
    }) => {
      // Nothing for the backend to file it under.
      if (!meetingId) return null;

      const body = {
        meetingId,
        attendeeId,
        appointmentId,
        errorType,
        errorMessage: capReportMessage(errorMessage),
        browserInfo: browserInfo || getBrowserInfo(),
        reconnectionAttempts,
        timestamp: new Date().toISOString(),
      };
      const hasStaffSession = !!sessionStorage.getItem("accessToken");

      // The staff instance redirects to /logout when a 401 can't be refreshed,
      // which would throw a patient out of their call, so patients use the
      // plain instance — always with tenant 0, even on a shared PC that still
      // has a staff member's tenant stored.
      const response = hasStaffSession
        ? await createAxiosInstance().post(
            `${getBaseUrl(tenantVal)}/report-error`,
            body
          )
        : await publicAxiosInstance.post(
            `/api/practice/v1/${getBaseUrl(PATIENT_REPORT_TENANT)}/report-error`,
            body
          );

      return response.data;
    },
    // A failed report must never reach the user: it showed "An error
    // occurred" over a call that had ended normally (KIN-1987).
    meta: { skipGlobalError: true },
    onError: (error) => {
      console.warn("Failed to report video error:", error);
    },
  });
};

/**
 * Get video service status
 * GET /{tenant}/video/meeting/service-status
 */
export const useVideoServiceStatus = (options = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["videoServiceStatus"],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${getBaseUrl(tenantVal)}/service-status`
      );
      return response.data;
    },
    enabled: !!tenantVal && options.enabled !== false,
    staleTime: 60000, // Cache for 1 minute
    retry: 1,
  });
};

/**
 * Helper to get browser info for error reporting
 */
// Most specific first: Edge and Opera user agents also contain "Chrome/", and
// Chrome's contains "Safari/" — checking Chrome first reported Edge as Chrome.
const BROWSER_TOKENS = [
  { name: "Edge", token: "Edg/" },
  { name: "Edge", token: "Edge/" },
  { name: "Opera", token: "OPR/" },
  { name: "Firefox", token: "Firefox/" },
  { name: "Firefox", token: "FxiOS/" },
  { name: "Chrome", token: "CriOS/" },
  { name: "Chrome", token: "Chrome/" },
  { name: "Safari", token: "Version/" },
];

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  const match = BROWSER_TOKENS.find(({ token }) => ua.includes(token));
  const browserName = match?.name ?? "Unknown";
  const browserVersion = match
    ? ua.split(match.token)[1]?.split(" ")[0] || "Unknown"
    : "Unknown";

  return {
    name: browserName,
    version: browserVersion,
    userAgent: ua,
    platform: navigator.platform,
  };
};

/**
 * Export browser info helper for external use
 */
export { getBrowserInfo };
